/**
 * Central route-access policy.
 *
 * One file declares who can reach every part of the app. The
 * ``RouteGuard`` evaluates these on every navigation and redirects
 * users who shouldn't be on a page. Page-level components stop
 * needing their own "if not canManage → redirect" useEffects; they
 * just render, trusting that the guard has already cleared them.
 *
 * Policies are checked in order, first match wins — put the most
 * specific patterns first (``/projects/:id/settings`` before
 * ``/projects/:id``) so children inherit only the matching rule.
 *
 * A policy can stall intentionally by returning ``allow: true`` when
 * it needs async context (e.g. project not loaded yet). The guard
 * re-runs on every context change, so the policy will be re-asked
 * once the data lands.
 */

export type PolicyUser = {
  id?: number;
  is_staff?: boolean;
  is_superuser?: boolean;
  active_organization?: number | null;
  active_organization_role?: string;
} | null;

export type PolicyProject = {
  id?: number;
  is_published?: boolean;
  created_by?: { id?: number } | null;
} | null;

export type PolicyContext = {
  user: PolicyUser;
  project: PolicyProject;
};

export type PolicyResult = { allow: true } | { allow: false; redirect: string; message?: string };

export type Policy = (ctx: PolicyContext) => PolicyResult;

// ── Predicate helpers ──────────────────────────────────────────────
// Kept tiny and pure so every rule reads as English.

export const isAuthenticated = (user: PolicyUser): boolean => !!user?.id;

export const isStaff = (user: PolicyUser): boolean => !!user && (!!user.is_staff || !!user.is_superuser);

export const hasOrgRole = (user: PolicyUser, roles: string[]): boolean => {
  if (!user) return false;
  if (user.is_staff || user.is_superuser) return true;
  return roles.includes(user.active_organization_role ?? "");
};

export const canManageOrg = (user: PolicyUser): boolean => hasOrgRole(user, ["OW", "AD", "MA"]);

export const isOrgOwnerOrAdmin = (user: PolicyUser): boolean => hasOrgRole(user, ["OW", "AD"]);

export const canManageProject = (user: PolicyUser, project: PolicyProject): boolean => {
  if (!user) return false;
  if (user.is_staff || user.is_superuser) return true;
  if (hasOrgRole(user, ["OW", "AD", "MA"])) return true;
  if (project?.created_by?.id && project.created_by.id === user.id) return true;
  return false;
};

// ── Redirect targets ───────────────────────────────────────────────
// Keeping these as constants so the whole app agrees on one "bounce
// destination" when gates fail. Rule of thumb: project-context gate
// failures bounce to ``/projects`` (or the project page); everything
// else (org, platform, account-level sections) bounces to ``/`` so
// the user lands on home rather than dumped into a project list they
// might not even be working on.

const BOUNCE_HOME_NOT_MANAGER = {
  redirect: "/",
  message: "You don't have access to that page.",
} as const;

const BOUNCE_HOME_NOT_STAFF = {
  redirect: "/",
  message: "That section is only available to platform administrators.",
} as const;

const BOUNCE_PROJECTS_NOT_MANAGER = {
  redirect: "/projects",
  message: "You don't have access to that page.",
} as const;

// ── Policies ───────────────────────────────────────────────────────
// Ordered from most-specific to least-specific. Each entry uses a
// React-Router path pattern; ``exact: false`` covers everything
// under the prefix unless a more specific entry intercepts it first.

// Reusable gates — kept as named constants so the policy table reads
// as an index, not a dump of closures.

const ownerAdminOnly: Policy = ({ user }) =>
  !isAuthenticated(user)
    ? { allow: true }
    : isOrgOwnerOrAdmin(user)
      ? { allow: true }
      : { allow: false, ...BOUNCE_HOME_NOT_MANAGER };

const managerPlus: Policy = ({ user }) =>
  !isAuthenticated(user)
    ? { allow: true }
    : canManageOrg(user)
      ? { allow: true }
      : { allow: false, ...BOUNCE_HOME_NOT_MANAGER };

// Same gate as ``managerPlus`` but bounces to ``/projects`` instead
// of ``/``. Use on project-context routes (e.g. project creation /
// templates) where dropping the user on home would lose the task
// they were trying to do.
const managerPlusToProjects: Policy = ({ user }) =>
  !isAuthenticated(user)
    ? { allow: true }
    : canManageOrg(user)
      ? { allow: true }
      : { allow: false, ...BOUNCE_PROJECTS_NOT_MANAGER };

const staffOnly: Policy = ({ user }) =>
  !isAuthenticated(user)
    ? { allow: true }
    : isStaff(user)
      ? { allow: true }
      : { allow: false, ...BOUNCE_HOME_NOT_STAFF };

// Project-scoped: waits for the project payload to arrive, then
// approves managers / creators.
const projectManagerOnly =
  (message: string): Policy =>
  ({ user, project }) => {
    if (!isAuthenticated(user)) return { allow: true };
    if (!project?.id) return { allow: true }; // still loading — re-run once it lands
    if (canManageProject(user, project)) return { allow: true };
    return { allow: false, redirect: `/projects/${project.id}`, message };
  };

// Plain annotators don't need the Data Manager / dashboard — when they
// open a project (from the Projects list, Discover, a deep link, or the
// back button) send them straight to the labeling stream. Managers,
// admins, owners, project creators and staff stay on the dashboard;
// reviewers stay too (their surface is the review stream). Waits for the
// project payload so ``canManageProject`` is accurate. Registered with
// ``exact: true`` on the BARE project route so only the dashboard
// redirects — the ``/data?labeling=1`` target is a different path, so
// there's no redirect loop.
const annotatorToLabelStream: Policy = ({ user, project }) => {
  if (!isAuthenticated(user)) return { allow: true };
  if (!project?.id) return { allow: true }; // still loading — re-run once it lands
  if (canManageProject(user, project)) return { allow: true }; // OW/AD/MA/creator/staff
  if (hasOrgRole(user, ["RE"])) return { allow: true }; // reviewers stay
  return { allow: false, redirect: `/projects/${project.id}/data?labeling=1` };
};

export const routePolicies: Array<{ pattern: string; exact?: boolean; policy: Policy }> = [
  // ── Platform (staff only) ──
  { pattern: "/platform", policy: staffOnly },

  // ── Organization sub-routes — most-specific first so they match
  //    before the broader ``/organization`` shell below. ──
  { pattern: "/organization/settings", policy: ownerAdminOnly },
  { pattern: "/organization/members", policy: managerPlus },
  { pattern: "/organization/quality", policy: ownerAdminOnly },
  { pattern: "/organization/review", policy: ownerAdminOnly },
  { pattern: "/organization/payouts", policy: ownerAdminOnly },
  { pattern: "/organization/activity", policy: ownerAdminOnly },
  { pattern: "/organization/health", policy: ownerAdminOnly },
  { pattern: "/organization/workspaces", policy: managerPlus },
  { pattern: "/organization/analytics", policy: managerPlus },

  // ── Organization shell fallback (OW/AD/MA). ──
  { pattern: "/organization", policy: managerPlus },

  // ── Project sub-routes — manager only, project-aware ──
  {
    pattern: "/projects/:id(\\d+)/settings",
    policy: projectManagerOnly("Only managers can change project settings."),
  },
  {
    pattern: "/projects/:id(\\d+)/members",
    policy: projectManagerOnly("Only managers can manage project members."),
  },
  {
    pattern: "/projects/:id(\\d+)/data/import",
    policy: projectManagerOnly("Only managers can import tasks."),
  },
  {
    pattern: "/projects/:id(\\d+)/data/export",
    policy: projectManagerOnly("Only managers can export data."),
  },
  {
    pattern: "/projects/:id(\\d+)/data/metrics",
    policy: projectManagerOnly("Only managers can view project metrics."),
  },
  {
    pattern: "/projects/:id(\\d+)/analytics",
    policy: projectManagerOnly("Only managers can view project analytics."),
  },

  // ── Bare project route: annotators → labeling stream. MUST be
  //    ``exact`` and come AFTER the sub-routes above so it only fires on
  //    the dashboard itself. ──
  {
    pattern: "/projects/:id(\\d+)",
    exact: true,
    policy: annotatorToLabelStream,
  },

  // ── Project templates — only folks who can create projects.
  //    Bounces to ``/projects`` on deny (project-context) instead of
  //    home, so the user stays in the project flow they came from. ──
  { pattern: "/project-templates", policy: managerPlusToProjects },

  // ── User profile — handled inside the page. Listed here as a no-op
  //    stub so future tightening lives in one file. ──
  {
    pattern: "/users/:userId(\\d+)/settings",
    policy: () => ({ allow: true }),
  },
];
